import { CURRENCY_CODE } from './currency';
import { getPricingCatalog } from '../config/pricing';

export const MAINTENANCE_CHARGE = 500;

export const FEATURE_PRICING_OPTIONS = [
  { key: 'onboardingSetup', label: 'Onboarding and Setup', price: 500 },
  { key: 'invoice', label: 'Invoice', price: 300 },
  { key: 'trainerStaffManagement', label: 'Trainer and Staff Management', price: 300 },
  { key: 'slotBooking', label: 'Slot Booking', price: 250 },
  { key: 'memberRetention', label: 'Member Retention', price: 250 },
];

export const toAmount = value => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const getLegacyPricing = () => ({
  currency: CURRENCY_CODE,
  maintenanceCharge: MAINTENANCE_CHARGE,
  features: FEATURE_PRICING_OPTIONS.map(feature => ({
    ...feature,
    selected: true,
  })),
  monthlyFeatureTotal: FEATURE_PRICING_OPTIONS.reduce((total, feature) => total + feature.price, 0),
  finalMonthlyCost: FEATURE_PRICING_OPTIONS.reduce((total, feature) => total + feature.price, MAINTENANCE_CHARGE),
  finalMonthlyCostEdited: false,
});

export const getDefaultOnboardingPricing = (tierId = 'small') => {
  const catalog = getPricingCatalog();
  const tier = catalog.tiers.find(tier => tier.id === tierId) || catalog.tiers[0];
  return calculateOnboardingPricing({
    catalogVersion: 2, tierId: tier.id, billingPeriod: 'monthly',
    currency: CURRENCY_CODE, maintenanceCharge: 0, coreMonthly: tier.monthly,
    annualDiscountPercent: catalog.annualDiscountPercent, finalMonthlyCostEdited: false,
    features: catalog.addons.map(addon => ({ key: addon.id, label: addon.name, price: addon.monthly, selected: false })),
  });
};

export const normalizeOnboardingPricing = pricing => {
  if (pricing?.catalogVersion === 2) return calculateOnboardingPricing({
    ...getDefaultOnboardingPricing(pricing.tierId), ...pricing,
    features: getDefaultOnboardingPricing(pricing.tierId).features.map(feature => ({
      ...feature, ...(pricing.features || []).find(saved => saved.key === feature.key),
    })),
  });
  const savedFeatures = new Map((pricing?.features || []).map(feature => [feature.key, feature]));
  const features = FEATURE_PRICING_OPTIONS.map(defaultFeature => ({
    ...defaultFeature,
    selected: false,
    ...(savedFeatures.get(defaultFeature.key) || {}),
  }));

  return calculateOnboardingPricing({
    ...getLegacyPricing(),
    ...(pricing || {}),
    currency: pricing?.currency || CURRENCY_CODE,
    features,
  });
};

export const calculateOnboardingPricing = pricing => {
  const base = pricing || getLegacyPricing();
  const monthlyFeatureTotal = (base.features || []).reduce(
    (total, feature) => total + (feature.selected ? toAmount(feature.price) : 0),
    0
  );
  const maintenanceCharge = base.catalogVersion === 2 ? 0 : toAmount(base.maintenanceCharge);
  const subtotal = Math.round((monthlyFeatureTotal + maintenanceCharge + (base.catalogVersion === 2 ? toAmount(base.coreMonthly) : 0)) * 100) / 100;
  const finalMonthlyCost = base.finalMonthlyCostEdited ? toAmount(base.finalMonthlyCost) : subtotal;
  const annualSavings = base.billingPeriod === 'annual' ? Math.round(finalMonthlyCost * 12 * toAmount(base.annualDiscountPercent)) / 100 : 0;

  return {
    ...base,
    currency: base.currency || CURRENCY_CODE,
    maintenanceCharge,
    monthlyFeatureTotal,
    finalMonthlyCost,
    annualSavings,
    billingTotal: base.billingPeriod === 'annual' ? Math.round((finalMonthlyCost * 12 - annualSavings) * 100) / 100 : finalMonthlyCost,
  };
};
